<template>
  <div>
    <div class="row">
      <div class="col-xs-12">
        <h1>Marketing Report</h1>
      </div>
    </div>

    <div class="row">
      <div class="col-xs-12">
        <div class="well">
          <form class="form-inline" @submit.prevent="applyFilters">
            <div class="form-group" style="margin-right: 10px;">
              <label for="mr-start-date">Start Date</label>
              <input type="date" id="mr-start-date" class="form-control" v-model="localFilters.start_date" :max="localFilters.end_date" required />
            </div>
            <div class="form-group" style="margin-right: 10px;">
              <label for="mr-end-date">End Date</label>
              <input type="date" id="mr-end-date" class="form-control" v-model="localFilters.end_date" :min="localFilters.start_date" required />
            </div>
            <div v-if="schools.length > 0" class="form-group" style="margin-right: 10px;">
              <label for="mr-schools">Location</label>
              <select id="mr-schools" class="form-control" v-model="selectedSchoolId" data-testid="marketing-report-location">
                <option :value="null">All locations</option>
                <option v-for="school in schools" :key="school.id" :value="school.id">{{ school.name }}</option>
              </select>
            </div>
            <button type="submit" class="btn btn-dc" data-testid="marketing-report-apply">Apply</button>
          </form>
        </div>
      </div>
    </div>

    <div v-if="error" class="row">
      <div class="col-xs-12">
        <div class="alert alert-danger">{{ error }}</div>
      </div>
    </div>

    <div v-if="loading" class="row">
      <div class="col-xs-12 text-center">
        <i class="fa fa-circle-o-notch fa-spin fa-2x"></i>
      </div>
    </div>

    <div v-if="reportData && !loading">
      <div v-if="!reportData.tracking_started_on" class="panel panel-default" data-testid="marketing-report-empty">
        <div class="panel-body">
          <p><strong>No marketing data yet.</strong></p>
          <p class="text-muted">
            Drive Scout starts recording where customers come from with their next online order.
            WordPress sites need version 0.2.34 or later of the Drive Scout plugin.
          </p>
        </div>
      </div>

      <div v-else>
        <p class="text-muted" data-testid="marketing-report-tracking">
          Tracking began {{ longDate(reportData.tracking_started_on) }}. Earlier orders aren't included.
          This report counts paid and part-paid online orders, so its totals differ from the Ecommerce dashboard.
        </p>

        <div class="row">
          <div class="col-md-3" v-for="card in cards" :key="card.label">
            <div class="panel panel-default">
              <div class="panel-body text-center">
                <h4 class="text-muted">
                  {{ card.label }}
                  <CalculationTooltip :text="card.tooltip" />
                </h4>
                <p class="h3">{{ card.value }}</p>
                <p class="text-muted small">{{ card.detail }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xs-12">
            <MarketingChannelChart :by-channel="reportData.by_channel" />
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <MarketingBreakdownTable
              title="Top Sources"
              test-id="marketing-report-sources"
              :rows="reportData.by_source"
              :name-columns="sourceColumns"
              empty-text="No orders with a known source in these dates."
            />
          </div>
          <div class="col-md-6">
            <MarketingBreakdownTable
              title="Top Campaigns"
              test-id="marketing-report-campaigns"
              :rows="reportData.by_campaign"
              :name-columns="campaignColumns"
              empty-text="No orders from a tagged campaign in these dates."
            />
          </div>
        </div>
      </div>
    </div>

    <MarketingWebsiteLeads
      v-if="reportData && reportData.website_leads.leads > 0"
      :leads="reportData.website_leads"
    />

    <MarketingCustomerSources :filters="filters" />
  </div>
</template>

<script>
import CalculationTooltip from '../scheduling-dashboard/components/CalculationTooltip.vue';
import { inDollars } from '../../../DriveScout/Billing/Teller';
import MarketingChannelChart from './components/MarketingChannelChart.vue';
import MarketingBreakdownTable from './components/MarketingBreakdownTable.vue';
import MarketingCustomerSources from './components/MarketingCustomerSources.vue';
import MarketingWebsiteLeads from './components/MarketingWebsiteLeads.vue';

function copyFilters(filters) {
  return Object.assign({}, filters, { school_ids: filters.school_ids.slice() });
}

export default {
  name: 'MarketingReport',
  components: {
    CalculationTooltip,
    MarketingChannelChart,
    MarketingBreakdownTable,
    MarketingCustomerSources,
    MarketingWebsiteLeads
  },
  props: {
    reportData: { type: Object, default: null },
    schools: { type: Array, default: function() { return []; } },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    filters: { type: Object, required: true },
    onFilterChange: { type: Function, required: true }
  },
  data: function() {
    return {
      localFilters: copyFilters(this.filters),
      sourceColumns: [{ key: 'source', label: 'Source' }],
      campaignColumns: [{ key: 'campaign', label: 'Campaign' }, { key: 'source', label: 'Source' }]
    };
  },
  computed: {
    // The API reads the school being viewed unless it is named locations, so "all" names every one.
    selectedSchoolId: {
      get: function() {
        return this.localFilters.school_ids.length === 1 ? this.localFilters.school_ids[0] : null;
      },
      set: function(schoolId) {
        this.localFilters.school_ids = schoolId === null
          ? this.schools.map(function(school) { return school.id; })
          : [schoolId];
      }
    },
    cards: function() {
      var summary = this.reportData.summary;
      var google = summary.from_google;

      return [
        {
          label: 'Orders',
          value: summary.orders.toLocaleString(),
          detail: 'Paid and part-paid online orders',
          tooltip: 'Online orders that are paid in full or part-paid, placed in the selected dates.'
        },
        {
          label: 'Order Value',
          value: inDollars(summary.order_value),
          detail: 'Full price of those orders',
          tooltip: 'The total of those orders at full price, including what part-paid customers still owe.'
        },
        {
          label: 'With a Known Source',
          value: summary.attributed_percent + '%',
          detail: summary.attributed_orders.toLocaleString() + ' of ' + summary.orders.toLocaleString() + ' orders',
          tooltip: 'Orders where we recorded an ad, a link or another website. The rest came directly, or we could not track them.'
        },
        {
          label: 'From Google (at least)',
          value: google.orders.toLocaleString(),
          detail: google.paid.toLocaleString() + ' paid, ' + google.organic.toLocaleString() + ' organic',
          tooltip: 'Orders we can trace to Google. A visitor who reached your shop through your own website can show as Direct or not tracked, so the real number can be higher.'
        }
      ];
    }
  },
  watch: {
    filters: {
      handler: function(newVal) {
        this.localFilters = copyFilters(newVal);
      },
      deep: true
    }
  },
  methods: {
    applyFilters: function() {
      this.onFilterChange(copyFilters(this.localFilters));
    },
    longDate: function(ymd) {
      var parts = ymd.split('-').map(Number);

      return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
};
</script>
